import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';

import { VIDEA_DEMO_USER_PPD } from '../constants';
import { usePreferences } from '../react-query/usePreferences';

export const determineIsPPDVideaDemoUser = (user: any): boolean => {
  const { primaryOrganizationId, primaryPracticeId } = user || {};

  return (
    primaryOrganizationId === VIDEA_DEMO_USER_PPD.organizationId &&
    primaryPracticeId === VIDEA_DEMO_USER_PPD.practiceId
  );
};

export enum PracticeFeatureFlagKey {
  HQ_DISABLED = 'HQ_DISABLED',
  PA_CARIES_ENABLED = 'PA_CARIES_ENABLED',
  DATE_TIME_ENABLED = 'DATE_TIME_ENABLED',
  CARIES_SUBCLASSIFICATION_ENABLED = 'CARIES_SUBCLASSIFICATION_ENABLED',
  TOOTH_THIRDS_ENABLED = 'TOOTH_THIRDS_ENABLED',
  RBL_ENABLED = 'RBL_ENABLED',
  CALCULUS_ENABLED = 'CALCULUS_ENABLED',
  PRL_ENABLED = 'PRL_ENABLED',
  IS_SALES_DEMO = 'IS_SALES_DEMO',
  CARIES_DEPTH_ENABLED = 'CARIES_DEPTH_ENABLED',
  VIDEA_DENTAL_ASSIST_ENABLED = 'VIDEA_DENTAL_ASSIST_ENABLED',
  FINDINGS_SIDEBAR_V2_ENABLED = 'FINDINGS_SIDEBAR_V2_ENABLED',
  PLANNING_MODE_DISABLED = 'PLANNING_MODE_DISABLED',
  NON_XRAY_VIEW_ENABLED = 'NON_XRAY_VIEW_ENABLED',
  DUAL_MODE_ENABLED = 'DUAL_MODE_ENABLED',
  PATIENT_VIEW_ENABLED = 'PATIENT_VIEW_ENABLED',
  TOOTH_PARTS_SEGMENTATIONS_ENABLED = 'TOOTH_PARTS_SEGMENTATIONS_ENABLED',
  DDAI_METRICS_ENABLED = 'DDAI_METRICS_ENABLED',
  DMF_ENABLED = 'DMF_ENABLED',
  ONBOARDING_ENABLED = 'ONBOARDING_ENABLED',
  DDAI_ROI_PAGE_V1_ENABLED = 'DDAI_ROI_PAGE_V1_ENABLED',
  DDAI_INSURANCE_PDF_ENABLED = 'DDAI_INSURANCE_PDF_ENABLED',
  REALTIME_INSURANCE_PDF_DISABLED = 'REALTIME_INSURANCE_PDF_DISABLED',
}

export enum OrganizationFeatureFlagKey {
  INSIGHTS_DASHBOARD_ENABLED = 'INSIGHTS_DASHBOARD_ENABLED',
  SALES_DEMO_INSIGHTS_MOCK_ENABLED = 'SALES_DEMO_INSIGHTS_MOCK_ENABLED',
}

export enum FeatureFlagType {
  ORGANIZATION = 'organization',
  PRACTICE = 'practice',
}
// TODO: Lint TS and JS files with different rule sets since JS rules don't understand TS.

export type FeatureFlag = {
  key: PracticeFeatureFlagKey;
  value: boolean;
};

/**
 * @param flagToCheck
 * @returns The flag if found, and otherwise undefined.
 */
export const useFeatureFlag = (
  flagToCheck: PracticeFeatureFlagKey | OrganizationFeatureFlagKey,
  featureFlagType: FeatureFlagType = FeatureFlagType.PRACTICE,
) => {
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);

  const flags = (
    featureFlagType === 'practice'
      ? user?.practiceFeatureFlags
      : user?.organizationFeatureFlags
  ) as
    | {
        key: PracticeFeatureFlagKey | OrganizationFeatureFlagKey;
        value: boolean;
      }[]
    | undefined;
  if (!flags) return undefined;
  return flags.find((f) => f.key === flagToCheck);
};

export const useCheckIfHeartlandPractice = () => {
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  return '6216af044f5e3f4982c9afdf' === user.primaryOrganizationId;
};

export const useCheckIfSagePractice = () => {
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  return '65e8fa791be90ad434481a87' === user.primaryOrganizationId;
};

export const useCheckIfDentalCorpPractice = () => {
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  return '6627d88f4230520a6cd4c3df' === user.primaryOrganizationId;
};

export const useCheckIfAspenPractice = () => {
  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  return '66748e559ec5c80b375640fc' === user.primaryOrganizationId;
};

export const useIsPatientViewEnabled = () => {
  const preferencesQuery = usePreferences();
  const isSegmentationEnabled = Boolean(
    preferencesQuery?.data?.segmentationEnabled,
  );

  return isSegmentationEnabled;
};
